/**
 * Pure helpers for TripReplayMap: VehicleReplayModel → playback data, plus the lookups the
 * animation loop runs every frame (index at a time, heading at a point). No React / Leaflet here.
 */
export const RED = '#ff5a5f';
export const AMBER = '#ffb35c';
export const GREEN = '#3ee0a3';

/** Trip-status markers: label, colour (map pin + timeline) and icon (tripReplayMarkers.js). */
export const STATUS_KINDS = {
  assigned: { label: 'Assigned', color: '#7ea8ff', icon: 'person' },
  accepted: { label: 'Accepted', color: '#5cc8ff', icon: 'check' },
  enroute: { label: 'Driver en route', color: '#ffb35c', icon: 'car' },
  location: { label: 'On location', color: '#ffe066', icon: 'pin' },
  boarded: { label: 'Boarded', color: '#b48cff', icon: 'person' },
  onride: { label: 'On ride', color: '#7ee0ff', icon: 'car' },
  departed: { label: 'Departed', color: '#a0e85b', icon: 'arrow' },
  dropped: { label: 'Dropped', color: '#ff7eb6', icon: 'arrow' },
  completed: { label: 'Completed', color: '#3ee0a3', icon: 'check' },
  noshow: { label: 'No show', color: '#ff5a5f', icon: 'cross' },
  other: { label: '', color: '#8e9aa6', icon: 'pin' },
};
const KIND_BY_STATUS_ID = { 102: 'enroute', 104: 'enroute', 106: 'enroute', 107: 'location', 108: 'boarded', 109: 'noshow', 110: 'noshow', 112: 'onride', 114: 'departed', 116: 'completed' };
const KIND_BY_TEXT = [[/en ?route/i, 'enroute'], [/location/i, 'location'], [/board/i, 'boarded'], [/no ?show/i, 'noshow'],
  [/on ride|process/i, 'onride'], [/depart/i, 'departed'], [/complet/i, 'completed']];
// API TripStatusTimes slots (T_TRIP_TRACKING times) — the main source of the status pins.
const STATUS_TIME_SLOTS = [
  ['assignedTimeDto', 'assigned'], ['acceptedTimeDto', 'accepted'], ['locationTimeDto', 'location'],
  ['boardedTimeDto', 'boarded'], ['departedTimeDto', 'dropped'], ['completedTimeDto', 'completed'],
];
// GPS-row StatusEvents only fill these kinds, when their TripStatusTimes slot is empty.
const STATUS_EVENT_FALLBACK = new Set(['location', 'boarded', 'completed', 'noshow']);
// Ride-row times: last resort, e.g. an older API without TripStatusTimes.
const MILESTONES = [
  ['assignedTime', 'assigned'], ['acceptedTime', 'accepted'], ['locationTime', 'location'],
  ['loadTime', 'boarded'], ['unloadTime', 'dropped'], ['completedTime', 'completed'],
];
const STACK_METERS = 20;      // markers this close to an earlier one are stacked above it
const MIN_STOP_SECS = 20;
const MIN_BEARING_M = 3;      // shorter segments give a noisy bearing — carry the previous one
const TURN_BLEND = 0.8;       // rotate toward the next segment only over the last 20% of this one
const GRADIENT_BUCKETS = 140;

/* ---------- formatting ---------- */
export const pick = (o, k) => (o == null ? undefined : o[k] !== undefined ? o[k] : o[k.charAt(0).toUpperCase() + k.slice(1)]);
export const toSec = (v) => { const p = String(v || '0:0:0').split(':').map(Number); return (p[0] || 0) * 3600 + (p[1] || 0) * 60 + (p[2] || 0); };
export const hms = (sec) => {
  sec = ((Math.round(sec) % 86400) + 86400) % 86400;
  return [Math.floor(sec / 3600), Math.floor(sec % 3600 / 60), sec % 60].map(n => String(n).padStart(2, '0')).join(':');
};
export const dur = (s) => {
  s = Math.round(s || 0);
  const h = Math.floor(s / 3600), m = Math.floor(s % 3600 / 60), x = s % 60;
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m ${String(x).padStart(2, '0')}s`;
};
export const niceSpan = (v) => (/^\d{1,2}:\d{2}(:\d{2})?$/.test(String(v || '')) ? dur(toSec(v)) : String(v || ''));
export const initials = (n) => String(n || '').replace(/\[.*?\]/g, '').trim().split(/\s+/).filter(Boolean)
  .map(w => w[0]).join('').slice(0, 2).toUpperCase() || '—';

/* GpsDateTime: dd/MM/yyyy HH:mm[:ss] [AM|PM], yyyy-MM-dd HH:mm:ss, ISO, or HH:mm:ss */
export function parseDT(s) {
  if (s == null || s === '') return null;
  if (typeof s === 'number') return new Date(s);
  s = String(s).trim();
  let m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?/i.exec(s);
  if (m) {
    let hh = +m[4]; if (m[7]) hh = hh % 12 + (/pm/i.test(m[7]) ? 12 : 0);
    return new Date(+m[3], +m[2] - 1, +m[1], hh, +m[5], +(m[6] || 0));
  }
  m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/.exec(s);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0));
  m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(s);
  if (m) return new Date(2000, 0, 1, +m[1], +m[2], +(m[3] || 0));
  const d = new Date(s);
  return isNaN(d) ? null : d;
}

/* ---------- geo ---------- */
const RAD = Math.PI / 180;
export function hav(a, b) {
  const dLat = (b[0] - a[0]) * RAD, dLng = (b[1] - a[1]) * RAD;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * RAD) * Math.cos(b[0] * RAD) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.sqrt(h));
}
/** Initial bearing a → b, degrees clockwise from north (0–360). */
export function bearing(a, b) {
  const p1 = a[0] * RAD, p2 = b[0] * RAD, dl = (b[1] - a[1]) * RAD;
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return (Math.atan2(y, x) / RAD + 360) % 360;
}
/** Interpolates between two headings along the shorter arc. */
export const blendHeading = (from, to, k) => (from + ((((to - from) % 360) + 540) % 360 - 180) * k + 360) % 360;

/* ---------- speed colours / status ---------- */
const speedBand = (v) => (v < 1 ? RED : v < 25 ? AMBER : GREEN);
export function colorAt(codes, speed) {
  if (!codes.length) return speedBand(speed);
  let hit = codes[0];
  for (const c of codes) if (speed >= c.min) hit = c;
  return hit.color;
}
export function statusFor(fix, speed) {
  const s = fix ? fix.status : '';
  if (/stop/i.test(s)) return ['Stopped', RED];
  if (/idle/i.test(s)) return ['Idle', AMBER];
  if (/mov|run/i.test(s)) return ['Moving', GREEN];
  return speed < 1 ? ['Stopped', RED] : speed < 25 ? ['Slow', AMBER] : ['Moving', GREEN];
}

/* ---------- per-frame lookups ---------- */
/**
 * Index i of the segment [f[i], f[i+1]] containing time t. Starts from the previous index
 * (`hint`) so normal playback is O(1); falls back to a binary search for seeks.
 */
export function indexAt(f, t, hint = 0) {
  const last = f.length - 2;
  let i = Math.max(0, Math.min(hint, last));
  if (f[i].t <= t && (i === last || f[i + 1].t > t)) return i;
  if (i < last && f[i + 1].t <= t && (i + 1 === last || f[i + 2].t > t)) return i + 1;
  let lo = 0, hi = last;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (f[mid].t <= t) lo = mid; else hi = mid - 1;
  }
  return lo;
}

/** Vehicle heading at fraction k of segment i: the road's direction, turning into the next segment near its end. */
export function headingAt(seg, i, k) {
  const h = seg[i];
  if (k <= TURN_BLEND || i + 1 >= seg.length) return h;
  return blendHeading(h, seg[i + 1], (k - TURN_BLEND) / (1 - TURN_BLEND));
}

/* ---------- VehicleReplayModel → playback data ---------- */
function parseFixes(rows) {
  let prevMs = -Infinity, shift = 0;
  const f = [];
  rows.forEach(r => {
    const lat = +pick(r, 'latitude'), lng = +pick(r, 'longitude');
    if (!isFinite(lat) || !isFinite(lng) || (lat === 0 && lng === 0)) return;
    const dt = parseDT(pick(r, 'gpsDateTime'));
    if (!dt) return;
    let ms = dt.getTime() + shift;
    if (ms < prevMs - 1000) { shift += 86400000; ms += 86400000; }   // time-only values crossing midnight
    prevMs = ms;
    f.push({
      ll: [lat, lng], speed: +pick(r, 'speed') || 0, course: +pick(r, 'course') || 0, ms,
      status: String(pick(r, 'status') || ''), address: pick(r, 'address') || '', remarks: pick(r, 'remarks') || '',
      stoppage: pick(r, 'stoppageTime') || '', over: !!pick(r, 'isOverSpeed'),
    });
  });
  return f;
}

/**
 * Direction of travel for each segment, from the drawn line itself rather than the GPS compass
 * (often 0 or stale). Parked jitter and very short hops keep the previous heading.
 */
export function segmentBearings(f) {
  const seg = new Array(Math.max(0, f.length - 1));
  let prev = null;
  for (let i = 0; i < f.length - 1; i++) {
    const a = f[i], b = f[i + 1];
    const moving = a.speed >= 1 || b.speed >= 1;
    if (moving && hav(a.ll, b.ll) >= MIN_BEARING_M) prev = bearing(a.ll, b.ll);
    seg[i] = prev;
  }
  // before the first usable segment: use the first known heading, else the GPS course
  const firstKnown = seg.find(x => x != null);
  for (let i = 0; i < seg.length && seg[i] == null; i++) seg[i] = firstKnown != null ? firstKnown : f[i].course;
  return seg;
}

/** Consecutive segments of the same colour merged into one polyline: { color, from, to } (fix indices, inclusive). */
export function colourRuns(f, colorOf) {
  const runs = [];
  for (let i = 0; i < f.length - 1; i++) {
    const color = colorOf(f[i]);
    const last = runs[runs.length - 1];
    if (last && last.color === color) last.to = i + 1;
    else runs.push({ color, from: i, to: i + 1 });
  }
  return runs;
}

export function findStops(f) {
  const runEnd = (i) => { let j = i; while (j + 1 < f.length && f[j + 1].speed < 1) j++; return j; };
  const stops = [];
  const backend = f.some(x => x.stoppage);
  for (let i = 0; i < f.length; i++) {
    if (backend ? !f[i].stoppage : f[i].speed >= 1) continue;
    const j = runEnd(i), len = f[j].t - f[i].t;
    const sub = f[i].remarks || f[i].address;
    if (backend) stops.push({ start: f[i].t, len: Math.max(len, toSec(f[i].stoppage) || 0), label: niceSpan(f[i].stoppage), ll: f[i].ll, sub });
    else if (len >= MIN_STOP_SECS) stops.push({ start: f[i].t, len, label: dur(len), ll: f[i].ll, sub });
    i = Math.max(i, j);
  }
  return stops;
}

function kindOf(statusId, text) {
  if (KIND_BY_STATUS_ID[statusId]) return KIND_BY_STATUS_ID[statusId];
  const hit = KIND_BY_TEXT.find(([re]) => re.test(text || ''));
  return hit ? hit[1] : 'other';
}

/** GPS position at playback time t, interpolated between the fixes either side. */
function positionAt(f, t) {
  const i = indexAt(f, t), a = f[i], b = f[i + 1];
  const k = b.t > a.t ? Math.max(0, Math.min(1, (t - a.t) / (b.t - a.t))) : 0;
  return [a.ll[0] + (b.ll[0] - a.ll[0]) * k, a.ll[1] + (b.ll[1] - a.ll[1]) * k];
}

/**
 * Trip-status markers, from (in order of preference) the API's `tripStatusTimes`, its GPS-row
 * `statusEvents`, then the ride row's own times — one per kind. Each is placed where the
 * vehicle was at that time; times outside the GPS recording are pinned at its first/last point
 * but keep their real time. Numbered in real-time order; markers sharing a spot are stacked.
 */
function findEvents(f, ride, end, model) {
  const first = new Date(f[0].ms);
  const byKind = new Map();
  const subFor = (kind) => (kind === 'location' ? pick(ride, 'pickupLocation')
    : kind === 'boarded' ? pick(ride, 'paxName')
      : kind === 'dropped' ? pick(ride, 'dropoffLocation') : '') || '';
  const add = (kind, dt, ll, text) => {
    if (!dt || byKind.has(kind)) return;
    const start = Math.max(0, Math.min(end, (dt.getTime() - f[0].ms) / 1000));
    const k = STATUS_KINDS[kind];
    byKind.set(kind, {
      ms: dt.getTime(), start, kind, color: k.color, icon: k.icon, label: k.label || text || 'Status',
      ll: ll || positionAt(f, start), sub: subFor(kind), time: hms(dt.getHours() * 3600 + dt.getMinutes() * 60 + dt.getSeconds()),
    });
  };

  const times = pick(model, 'tripStatusTimes');
  STATUS_TIME_SLOTS.forEach(([slot, kind]) => {
    const s = pick(times, slot);
    if (!s) return;
    const id = +(pick(s, 'tripStatusId') || pick(s, 'tripStasusId')) || 0;
    add(kind === 'completed' && (id === 109 || id === 110) ? 'noshow' : kind, parseDT(pick(s, 'tripTime')), null, pick(s, 'tripStatus'));
  });

  (pick(model, 'statusEvents') || []).forEach(e => {
    const status = pick(e, 'status') || '';
    const kind = kindOf(+pick(e, 'tripStatusId') || 0, status);
    if (!STATUS_EVENT_FALLBACK.has(kind)) return;
    const lat = +pick(e, 'latitude'), lng = +pick(e, 'longitude');
    add(kind, parseDT(pick(e, 'gpsDateTime')), lat && lng && isFinite(lat) && isFinite(lng) ? [lat, lng] : null, status);
  });

  MILESTONES.forEach(([key, kind]) => {
    const dt = parseDT(pick(ride, key));
    if (dt && dt.getFullYear() === 2000) dt.setFullYear(first.getFullYear(), first.getMonth(), first.getDate());
    add(kind, dt, null, '');
  });

  const events = [...byKind.values()].sort((x, y) => x.ms - y.ms);
  events.forEach((e, i) => {
    e.n = i + 1;
    e.stack = events.slice(0, i).filter(p => hav(p.ll, e.ll) < STACK_METERS).length;
  });
  return events;
}

function summarize(model, f, dist, end) {
  const num = (k) => { const v = pick(model, k); return v == null || v === '' ? null : +v; };
  const str = (k) => pick(model, k) || '';
  const secsBy = (re) => f.reduce((a, x, i) => a + (i < f.length - 1 && re.test(x.status) ? f[i + 1].t - x.t : 0), 0);
  const moving = f.filter(x => x.speed >= 1);
  const hsum = dist[dist.length - 1] / 1000;
  const span = (strKey, secKey, fallback) => (str(strKey) ? niceSpan(str(strKey)) : dur(num(secKey) != null ? num(secKey) : fallback));
  return {
    km: num('totalDistance') != null ? num('totalDistance') : hsum,
    hsum,
    duration: span('totalTime', 'totalTimeSecs', end),
    moving: span('totalMovingTime', 'totalMovingTimeSecs', secsBy(/mov|run/i)),
    idle: span('totalIdleTime', 'totalIdleTimeSecs', secsBy(/idle/i)),
    stopped: span('totalStoppedTime', 'totalStoppedTimeSecs', secsBy(/stop/i)),
    avg: num('averageSpeed') != null ? num('averageSpeed') : (moving.length ? moving.reduce((a, x) => a + x.speed, 0) / moving.length : 0),
    max: num('maxSpeed') != null ? num('maxSpeed') : moving.reduce((a, x) => Math.max(a, x.speed), 0),
  };
}

function gradient(f, codes, end) {
  const css = [];
  let i = 0;
  for (let b = 0; b < GRADIENT_BUCKETS; b++) {
    const tb = end * b / (GRADIENT_BUCKETS - 1);
    while (i < f.length - 2 && f[i + 1].t <= tb) i++;   // buckets ascend, so one forward cursor
    css.push(`${colorAt(codes, f[i].speed)} ${(b / (GRADIENT_BUCKETS - 1) * 100).toFixed(2)}%`);
  }
  return `linear-gradient(90deg, ${css.join(', ')})`;
}

/**
 * "Not fully driven" hint, decided by the API: `tripStatusTimes.hasTripStatusValidTimeDuration`
 * is false when Assigned, Boarded and Dropped/Completed were all recorded within a few minutes.
 * Returns { mins, from, to } for the message, or null.
 */
function manualCompletionHint(model, events) {
  const st = pick(model, 'tripStatusTimes');
  if (!st || pick(st, 'hasTripStatusValidTimeDuration') !== false) return null;
  const first = events.find(e => e.kind === 'assigned');
  const ends = events.filter(e => e.kind === 'dropped' || e.kind === 'completed');
  const last = ends[ends.length - 1];
  return {
    mins: Math.max(1, Math.ceil((+pick(st, 'tripStatusDurationSeconds') || 0) / 60)),
    from: first ? first.time.slice(0, 5) : '',
    to: last ? last.time.slice(0, 5) : '',
  };
}

export function buildData(model, ride) {
  const codes = (pick(model, 'speedwiseColorCodes') || [])
    .map(c => ({ min: +pick(c, 'minSpeed') || 0, max: +pick(c, 'maxSpeed') || 0, color: pick(c, 'polylineColor'), name: pick(c, 'colorName') || '' }))
    .filter(c => c.color).sort((a, b) => a.min - b.min);
  const f = parseFixes(pick(model, 'replayData') || []);
  if (f.length < 2) return null;

  const first = new Date(f[0].ms);
  f.forEach(x => { x.t = (x.ms - f[0].ms) / 1000; });
  const dist = [0];
  for (let i = 1; i < f.length; i++) dist.push(dist[i - 1] + hav(f[i - 1].ll, f[i].ll));
  const end = f[f.length - 1].t;
  const events = findEvents(f, ride, end, model);

  return {
    fixes: f,
    dist,
    end,
    seg: segmentBearings(f),
    runs: colourRuns(f, x => colorAt(codes, x.speed)),
    stops: findStops(f),
    events,
    manualHint: manualCompletionHint(model, events),
    codes,
    summary: summarize(model, f, dist, end),
    overCount: f.filter(x => x.over).length,
    t0: first.getHours() * 3600 + first.getMinutes() * 60 + first.getSeconds(),
    date0: first,
    gradient: gradient(f, codes, end),
  };
}
